import { useState, useMemo } from 'react'
import { format, subDays, subMonths, subYears } from 'date-fns'
import { PiCalendarBlank, PiCaretDown, PiCaretUp, PiX } from 'react-icons/pi'
import { Popover, PopoverContent, PopoverTrigger } from 'components/UI/popover'
import { Calendar } from 'components/UI/calendar'
import { cn } from 'components/UI/cn'
import FbButton from 'components/FbUI/FbButton'
import { filterTriggerClasses } from 'components/FbUI/filterTriggerClasses'

export type DateRangeValue = { from: Date; to?: Date } | undefined

interface DateRangePreset {
  label: string
  value: string
  getRange: () => { from: Date; to: Date }
}

const getPresets = (): DateRangePreset[] => {
  const end = new Date()
  end.setHours(23, 59, 59, 999)

  const startOfDay = (d: Date) => {
    const x = new Date(d)
    x.setHours(0, 0, 0, 0)
    return x
  }

  return [
    {
      label: 'Last Week',
      value: 'last_week',
      getRange: () => ({ from: startOfDay(subDays(end, 7)), to: end }),
    },
    {
      label: 'Last Month',
      value: 'last_month',
      getRange: () => ({ from: startOfDay(subMonths(end, 1)), to: end }),
    },
    {
      label: 'Last 3 Months',
      value: 'last_3_months',
      getRange: () => ({ from: startOfDay(subMonths(end, 3)), to: end }),
    },
    {
      label: 'Last 6 Months',
      value: 'last_6_months',
      getRange: () => ({ from: startOfDay(subMonths(end, 6)), to: end }),
    },
    {
      label: 'Last Year',
      value: 'last_year',
      getRange: () => ({ from: startOfDay(subYears(end, 1)), to: end }),
    },
  ]
}

/** Reusable date range picker with a 2-month calendar grid on the left and
 *  a Quick Select preset list on the right. Selections auto-apply via
 *  `onChange` so clicking outside the popover preserves the picked range;
 *  the Apply footer button just dismisses. */
export function DateRangePicker({
  value,
  onChange,
}: {
  value: DateRangeValue
  onChange: (range: DateRangeValue) => void
}) {
  const [open, setOpen] = useState(false)

  // Detect if the current value matches a preset (so we can highlight it
  // in the side list).
  const matchingPreset = useMemo(() => {
    if (!value?.from || !value?.to) return null
    return (
      getPresets().find((p) => {
        const r = p.getRange()
        return (
          Math.abs(r.from.getTime() - value.from.getTime()) <
            24 * 3600 * 1000 &&
          Math.abs(r.to.getTime() - (value.to as Date).getTime()) <
            24 * 3600 * 1000
        )
      })?.value ?? null
    )
  }, [value])

  const triggerLabel = (() => {
    if (!value?.from) return 'All time'
    if (matchingPreset) {
      return getPresets().find((p) => p.value === matchingPreset)?.label
    }
    if (value.to)
      return `${format(value.from, 'd MMM yyyy')} – ${format(value.to, 'd MMM yyyy')}`
    return format(value.from, 'd MMM yyyy')
  })()

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {/* Trigger shares its className construction with FilterButton via
            filterTriggerClasses() so the active/open strokes are byte-identical
            to the sibling TeamMember and value/range filters on the same row. */}
        <FbButton
          variant="secondary"
          type="button"
          className={filterTriggerClasses({
            open,
            isActive: !!value?.from,
          })}
        >
          <div className="w-full flex items-center gap-2 text-sm font-medium">
            <PiCalendarBlank
              size={16}
              className={cn(
                'flex-shrink-0',
                value?.from ? 'text-brand-active' : 'text-neutral-500'
              )}
            />
            <span className="flex-1 text-left truncate">{triggerLabel}</span>
            {value?.from ? (
              <span
                role="button"
                tabIndex={0}
                aria-label="Clear date range"
                onClick={(e) => {
                  e.stopPropagation()
                  onChange(undefined)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    e.stopPropagation()
                    onChange(undefined)
                  }
                }}
                className="flex-shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full text-brand-active hover:bg-basil-100"
              >
                <PiX size={14} />
              </span>
            ) : open ? (
              <PiCaretUp size={14} className="text-neutral-500 flex-shrink-0" />
            ) : (
              <PiCaretDown
                size={14}
                className="text-neutral-500 flex-shrink-0"
              />
            )}
          </div>
        </FbButton>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto !p-0 !rounded-xl !border-neutral-200 !shadow-lg overflow-hidden"
        align="start"
      >
        <div className="flex">
          {/* Left: calendar */}
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={value?.from}
            selected={value}
            onSelect={(newRange) => {
              if (!newRange?.from) return
              onChange(newRange as { from: Date; to?: Date })
            }}
            numberOfMonths={2}
            classNames={{
              // Override the base `day_selected` (which paints every selected
              // day green) so middle-range days stay neutral. The brand-active
              // circle is reintroduced explicitly on start/end below.
              day_selected: 'rounded-full text-neutral-800',
              day_range_start:
                'day-range-start !bg-brand-active !text-white rounded-full hover:!bg-brand-active hover:!text-white focus:!bg-brand-active focus:!text-white',
              day_range_end:
                'day-range-end !bg-brand-active !text-white rounded-full hover:!bg-brand-active hover:!text-white focus:!bg-brand-active focus:!text-white',
              day_range_middle:
                'day-range-middle !bg-transparent !text-neutral-800 rounded-none hover:!bg-transparent',
              cell: 'h-9 w-9 text-center text-sm p-0 relative focus-within:relative focus-within:z-20 [&:has(.day-range-middle)]:bg-neutral-100 [&:has(.day-range-start)]:bg-gradient-to-r [&:has(.day-range-start)]:from-transparent [&:has(.day-range-start)]:from-50% [&:has(.day-range-start)]:to-neutral-100 [&:has(.day-range-start)]:to-50% [&:has(.day-range-end)]:bg-gradient-to-l [&:has(.day-range-end)]:from-transparent [&:has(.day-range-end)]:from-50% [&:has(.day-range-end)]:to-neutral-100 [&:has(.day-range-end)]:to-50%',
            }}
          />

          {/* Right: quick-select list */}
          <div className="w-[160px] border-l border-neutral-200 p-1.5 flex flex-col">
            <div className="text-xs font-semibold text-neutral-500 px-2 pt-1.5 pb-1">
              Quick Select
            </div>
            {getPresets().map((preset) => {
              const active = matchingPreset === preset.value
              return (
                <button
                  type="button"
                  key={preset.value}
                  onClick={() => onChange(preset.getRange())}
                  className={cn(
                    'w-full text-left px-2 py-2 text-sm rounded-lg transition-colors',
                    'hover:bg-neutral-100',
                    active
                      ? 'bg-neutral-100 text-neutral-900 font-semibold'
                      : 'text-neutral-700'
                  )}
                >
                  {preset.label}
                </button>
              )
            })}
          </div>
        </div>

        {/* Footer: Reset left, Cancel + Apply right */}
        <div className="flex items-center justify-between gap-2 px-3 py-3 border-t border-neutral-200">
          <button
            type="button"
            onClick={() => onChange(undefined)}
            disabled={!value}
            className={cn(
              'text-sm font-medium transition-colors',
              !value
                ? 'text-neutral-400 cursor-not-allowed'
                : 'text-brand-active hover:text-brand-bright'
            )}
          >
            Reset
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="px-4 h-9 inline-flex items-center justify-center rounded-lg border border-neutral-300 bg-white text-sm font-medium text-neutral-700 hover:bg-neutral-50 hover:border-neutral-400 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="px-4 h-9 inline-flex items-center justify-center rounded-lg bg-brand-bright text-white text-sm font-medium hover:bg-brand-active active:bg-brand-active transition-colors"
            >
              Apply
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
